/**
 * Camera-collision blockers for enclosed interiors (camCollision.ts `Blocker`, registered through
 * DungeonBuild.cameraBlockers). Walls, posts and colonnade pillars block up to their cutaway
 * height only: above it the cutaway (cutaway.ts) removes whatever stands between the camera and
 * the party, so the default high camera is never pulled in by a near wall, while a close camera
 * still can't slide through the solid stub. Lintels, ceilings, beams and roofs are left out (the
 * cutaway hides them; the cast only knows vertical quads). Tall authored props block like before.
 */
import type { Vec2 } from '@keystone/sim';
import type { Blocker } from '../camCollision';
import type { ArchLayout } from './arch';

export function enclosedBlockers(arch: ArchLayout): Blocker[] {
  const out: Blocker[] = [];
  for (const w of arch.walls) out.push({ a: w.a, b: w.b, top: Math.min(w.top, w.cutY) });
  for (const p of arch.posts) pushPoly(out, square(p.pos, p.size / 2), Math.min(p.top, p.cutY));
  for (const h of arch.hints) {
    if (h.spec.floorDecal || h.spec.opening) continue;
    const top = h.y + h.spec.h * h.scale;
    if (top - h.y < 1.2 || h.foot.length < 3) continue;
    pushPoly(out, h.foot, top);
  }
  return out;
}

function square(c: Vec2, h: number): Vec2[] {
  return [
    { x: c.x - h, y: c.y - h },
    { x: c.x + h, y: c.y - h },
    { x: c.x + h, y: c.y + h },
    { x: c.x - h, y: c.y + h },
  ];
}

function pushPoly(out: Blocker[], pts: readonly Vec2[], top: number): void {
  for (let i = 0; i < pts.length; i++) out.push({ a: pts[i]!, b: pts[(i + 1) % pts.length]!, top });
}
